import { configAtom } from "@/config.ts";
import type { SiteConfig } from "@/config.types.ts";
import type { ResourceType } from "@/modules/api/resourceTypes.tsx";
import { eBillingAccountQueryAtom, type BillingAccountWithType } from "@/modules/billing/billingAccountQuery.ts";
import { hasProcessorWithAutoTopup } from "@/modules/billing/paymentProcessorsLogic.tsx";
import { softInvariant } from "@/modules/error/errorStream.ts";
import { queryThen } from "@/utils/query/mergeQueries.ts";
import type {
    BillingAccount,
    BillingAccountCardData,
    BillingAccountCommonCardData,
    BillingAccountEligiblePromotion,
    BillingAccountKycInfo,
    BillingAccountStatus,
} from "@warrenio/api-spec/spec.oats.gen";
import { KycProcessStatusEnum } from "@warrenio/api-spec/spec/billingEnums";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { atom } from "jotai/vanilla";
import { first, isEmpty, partition, sortBy } from "remeda";

// XXX: is post-pay always a "card"?
export type PayType = "card" | "invoice" | "invoice_deactive" | "invoice_pending" | "prepay";

export type AccountClosedReason =
    | "insufficient_funds"
    | "invoice_verification"
    | "suspended"
    | "pending"
    | "missing_payment_method";

/** Action that *must* be done to resolve issue with a billing account */
export type AccountCallToAction =
    | "must_top_up"
    | "add_payment_method"
    | "contact_support"
    | "pending"
    | "wait_for_verification"
    | null;

/** Action to propose to user (for a billing account). Includes non-mandatory actions. */
export type AccountNextAction = AccountCallToAction | "top_up";

/** Global call to action for entire user shown in eg. Top Nav */
export type BillingCallToAction =
    | { action: "create_account" }
    | { action: "contact_support"; account: EBillingAccount }
    | { action: "pending" }
    // Billing-account specific actions (for the first account, with the default account preferred)
    | { action: Exclude<AccountCallToAction, null>; account: EBillingAccount }
    | { action: null };

/** Fields shared between the REST and GraphQL API */
export interface CommonBillingAccountFields {
    id: BillingAccount["id"];
    title: BillingAccount["title"];
    is_active: BillingAccount["is_active"];
    restriction_level: BillingAccount["restriction_level"];
    is_deleted: BillingAccount["is_deleted"];
    allow_debt: BillingAccount["allow_debt"];
    running_totals: {
        ongoing: number;
        ongoing_negative_since?: number;
        subtotal: number;
        discount_amount: number;
    };
    is_recurring_payment_enabled: BillingAccount["is_recurring_payment_enabled"];
    paying_by_invoice: BillingAccount["paying_by_invoice"];
    additional_data?: BillingAccount["additional_data"];
    primary_card?: {
        type: BillingAccountCardData["type"];
        is_verification_pending?: BillingAccountCommonCardData["is_verification_pending"];
    };
    send_invoice_email?: BillingAccount["send_invoice_email"];
    status?: BillingAccount["status"];
}

/**
 * Common base for user and GraphQL billing accounts.
 *
 * @see {@link EBillingAccount} for more
 */
export abstract class ECommonBillingAccount {
    protected abstract readonly _account: CommonBillingAccountFields;

    //#region Simple properties
    get account() {
        return this._account;
    }

    get id() {
        return this._account.id;
    }

    get title() {
        return this._account.title;
    }
    //#endregion

    //#region Account state
    get status(): BillingAccountStatus {
        return (
            this._account.status ??
            // Backwards compatibility
            (this._account.is_active ? "ACTIVE" : "SUSPENDED")
        );
    }

    // NB: no `isClosed` method to avoid redundancy with `!isClosed == isOpen` and make it easier to search for references.
    // Use `!isOpen` instead.

    /** Can create resources */
    get isOpen() {
        const { restriction_level } = this._account;
        return restriction_level === "CLEAR" || restriction_level === "LIMITED";
    }

    get isLimited() {
        return this._account.restriction_level === "LIMITED";
    }
    //#endregion

    //#region Payment types
    get payType(): PayType {
        if (this.isAnyInvoice) {
            return this.isVerificationPending
                ? "invoice_pending"
                : this.isInvoiceVerified
                  ? "invoice"
                  : "invoice_deactive";
        } else if (this.isPostPay) {
            return "card";
        } else {
            return "prepay";
        }
    }

    get isPostPay() {
        return this._account.allow_debt;
    }

    get isPrePay() {
        return !this._account.allow_debt;
    }

    get hasPaymentMethodAttached() {
        return !!this._account.primary_card;
    }

    protected get isVerificationPending() {
        return this._account.primary_card?.is_verification_pending === true;
    }

    protected get isAnyInvoice() {
        // XXX: Check `this._account.paying_by_invoice` instead?
        // (but see comment in `validate()`)
        return this._account.primary_card?.type === "paying_by_invoice";
    }

    get isInvoiceVerified() {
        return this._account.paying_by_invoice;
    }
    //#endregion

    get closedReason(): AccountClosedReason | null {
        if (this.isOpen) {
            return null;
        }

        switch (this.status) {
            case "PENDING_VALIDATION":
                return "pending";
            case "SUSPENDED":
                return "suspended";
            case "ACTIVE":
                switch (this.payType) {
                    case "card":
                        return "missing_payment_method";
                    case "invoice":
                    case "invoice_deactive":
                        // Happens when invoice account is manually closed
                        return "missing_payment_method";
                    case "invoice_pending":
                        return "invoice_verification";
                    case "prepay":
                        return "insufficient_funds";
                    default:
                        return exhaustiveSwitchCheck(this.payType);
                }
            default:
                return exhaustiveSwitchCheck(this.status);
        }
    }

    get ongoingAmount(): number {
        return this._account.running_totals?.ongoing ?? 0;
    }
}

/**
 * Wraps a billing account with useful business logic methods.
 *
 * Prefer it instead of using {@link BillingAccountWithType} directly.
 */
export class EBillingAccount extends ECommonBillingAccount {
    constructor(
        protected readonly _account: BillingAccountWithType,
        private readonly config: SiteConfig,
    ) {
        super();
        this.validate();
    }

    // Simple wrapper getter, to easily find references from external code that uses the raw account data
    get account() {
        return this._account;
    }

    get isDefault() {
        return this._account.is_default;
    }

    /** What the user must do now for this account. */
    get callToAction(): AccountCallToAction {
        // TODO: What is the call to action when the account is limited and post-pay/invoice?
        switch (this.closedReason) {
            case "insufficient_funds":
                return "must_top_up";
            case "missing_payment_method":
                return "add_payment_method";
            case "suspended":
                return "contact_support";
            case "invoice_verification":
                return "wait_for_verification";
            case "pending":
                return "pending";
            case null:
                return null;
            default:
                return exhaustiveSwitchCheck(this.closedReason);
        }
    }

    /** What the user must do or can do next for this account. */
    get nextAction(): AccountNextAction {
        const { callToAction } = this;
        if (callToAction != null) {
            return callToAction;
        }
        if (this.canTopUp) {
            return "top_up";
        }
        // IDEA: Enable automated top up?
        return null;
    }

    get canAddPaymentMethod() {
        return this.status === "ACTIVE";
    }

    get canDeletePaymentMethod() {
        return !(this.config.billingAccountValidationActive && this.status === "PENDING_VALIDATION");
    }

    //#region Post-payment
    get activeCard() {
        const { primary_card } = this._account;
        // XXX: does this need to check `can_pay`? (which would be the same as primary_card.is_verified)
        if (this.isOpen && this.isPostPay && primary_card) {
            return primary_card;
        }
        return null;
    }

    get hasVerifiedCard() {
        return this._account.can_pay;
    }

    get unpaidAmount(): number | null {
        return this.isPostPay ? this._account.unpaid_amount : null;
    }
    //#endregion

    /** @returns The (positive) amount of credit remaining, if there is any, `null` otherwise. */
    get remainingCredit(): number | null {
        const {
            running_totals: { ongoing, credit_available },
        } = this._account;
        // Check for `credit_available` to make sure the `ongoing` amount reflects any actual remaining credit (and not *just* the total usage)
        return credit_available > 0 && ongoing > 0 ? ongoing : null;
    }

    //#region Pre-payment
    get canTopUp() {
        return this.isPrePay && this.status === "ACTIVE";
    }

    get supportsAutomatedTopUps() {
        return (
            this.isPrePay &&
            this.config.recurringPaymentsEnabled &&
            // XXX: This should check `method.isAutomated` instead, but methods are not accessible here currently
            hasProcessorWithAutoTopup(this.config.paymentProcessors)
        );
    }
    //#endregion

    get eligiblePromotions(): BillingAccountEligiblePromotion[] {
        // NB: Backend does not seem to check for suspended accounts for this field, so double-check here
        return this.status === "SUSPENDED" ? [] : (this._account.eligible_promotions ?? []);
    }

    get canShareReferral() {
        return !!this.referralShareCode;
    }

    get referralShareCode() {
        const { referral_share_code } = this._account;
        return this.config.referralCodeEnabled && referral_share_code ? referral_share_code : null;
    }

    get isKycProcessStarted(): boolean {
        return this._account.kyc_info?.status === KycProcessStatusEnum.STARTED;
    }

    get isKycProcessCompleted(): boolean {
        return this._account.kyc_info?.status === KycProcessStatusEnum.COMPLETED;
    }

    get isKycApprovedByProvider(): boolean {
        return this._account.kyc_info?.status === KycProcessStatusEnum.APPROVED;
    }

    get isKycRejectedByProvider(): boolean {
        return this._account.kyc_info?.status === KycProcessStatusEnum.REJECTED;
    }

    get kycInfo(): BillingAccountKycInfo | undefined {
        return this._account.kyc_info;
    }

    /** Run consistency self-checks */
    private validate() {
        if (!import.meta.env.DEV) {
            return;
        }

        // NB: If any of these fail:
        // -    Check if it's a problem with these checks, or the actual data.
        // -    Leave a comment explaining the case here.
        // -    Create a story / situation to check that it is properly handled in the UI.

        const obj = this._account;

        const isSuspended = this.status === "SUSPENDED";
        const isActive = this.status === "ACTIVE";

        softInvariant(isActive || !this.isOpen, "Inactive account must be closed", obj);
        softInvariant(isActive || !this._account.can_pay, "Inactive account must not have `can_pay`", obj);

        // softInvariant(!this.isAnyInvoice || this.isPostPay, "Invoice account must be post-pay", obj);
        softInvariant(!this.isInvoiceVerified || this.isAnyInvoice, "Invoice verified must have invoice enabled", obj);
        softInvariant(
            !this._account.paying_by_invoice || this._account.primary_card?.is_verified === true,
            "Invoice verified states must match",
            obj,
        );

        const noPromotions = isEmpty(this._account.eligible_promotions ?? []);
        softInvariant(noPromotions || !isSuspended, "Suspended account should not have promotions", obj);
        softInvariant(noPromotions || !this.isAnyInvoice, "Invoice account should not have promotions", obj);
    }
}

export function getDefaultAccount(items: readonly EBillingAccount[]): EBillingAccount | undefined {
    return items.find((s) => s.isDefault) ?? items[0];
}

export type AccountCreateDisabledReason = "suspended" | null;

/** Top-level class for the entire user's billing state (for all billing accounts) */
export class EBillingState {
    public readonly defaultAccount: EBillingAccount | undefined;

    constructor(
        public readonly accounts: readonly EBillingAccount[],
        private readonly config: SiteConfig,
    ) {
        accounts = sortBy(
            accounts,
            (a) => (a.isOpen ? 1 : 2),
            (a) => a.title,
        );
        const [defaults, nonDefaults] = partition(accounts, (a) => a.isDefault);
        // QUESTION: Should we fall back to the first non-default account if there is no default?
        this.defaultAccount = first(defaults) ?? first(nonDefaults);
        // Always place default first, so it would be picked first in `.find()` etc. below
        this.accounts = [...defaults, ...nonDefaults];
    }

    get defaultIndex() {
        return this.accounts.length > 0 ? 0 : undefined;
    }

    get suspendedAccount() {
        return this.accounts.find((a) => a.status === "SUSPENDED");
    }

    get pendingAccount() {
        return this.accounts.find((a) => a.status === "PENDING_VALIDATION");
    }

    get createDisabledReason(): AccountCreateDisabledReason {
        return this.suspendedAccount ? "suspended" : null;
    }

    get firstAccountCredit() {
        return this.accounts.length === 0 ? this.config.firstAccountCredit : null;
    }

    get canEnterReferralCode() {
        // NB: Decision from WRN-223 to hide referral code entry for non-first accounts to prevent "infinite referral
        // stacking" for post-pay accounts, even though the backend does not enforce this.
        return this.accounts.length === 0;
    }

    get callToAction(): BillingCallToAction {
        if (this.accounts.length === 0) {
            return { action: "create_account" };
        }

        if (this.pendingAccount) {
            return { action: "pending" };
        }

        // XXX: This does not probably handle deleted *and* suspended accounts correctly
        if (this.suspendedAccount) {
            return { action: "contact_support", account: this.suspendedAccount };
        }

        const hasOpenAccount = this.accounts.some((a) => a.isOpen);
        if (hasOpenAccount) {
            return { action: null };
        }

        const hasVerifiedCard = this.accounts.some((a) => a.hasVerifiedCard);
        if (hasVerifiedCard) {
            return { action: null };
        }

        // QUESTION: Should we even suggest actions for non-default accounts?
        const account = this.accounts.find((a) => a.callToAction != null);
        if (account) {
            return { action: account.callToAction!, account };
        }

        return { action: null };
    }

    canCreateResource(resourceType: ResourceType) {
        switch (resourceType) {
            case "billing_account":
            case "api_token":
            case "access_delegation":
            case "access_impersonation":
                return true;

            case "virtual_machine":
            case "bare_metal":
            case "load_balancer":
            case "bucket":
            case "managed_service":
            case "vpc":
            case "ip_address":
            case "firewall":
            case "disk":
            case "storage_account":
                return this.accounts.some((a) => a.isOpen);

            default:
                exhaustiveSwitchCheck(resourceType);
        }
    }
}

export const billingStateAtom = atom((get) =>
    queryThen(get(eBillingAccountQueryAtom), (a) => new EBillingState([...a.values()], get(configAtom))),
);

// cspell:words deactive
